import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl, fmtIDR } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState } from "@/components/common";
import { Checkbox } from "@/components/ui/checkbox";
import { STATUS, carLabel, can } from "@/lib/labels";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "h-10 bg-[#0F0F12] border-[#1A1A22] focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
const labelCls = "text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400";

function CarFormDialog({ open, onOpenChange, branches, car, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [photo, setPhoto] = useState([]);
  const [busy, setBusy] = useState(false);
  const visibleBranches = user?.role === "admin_cabang" ? branches.filter((b) => b.id === user.branch_id) : branches;

  useEffect(() => {
    if (open) {
      setPhoto([]);
      setForm(car
        ? {
            merk: car.merk, model: car.model, year: car.year, plate: car.plate || "", color: car.color || "", km: car.km || 0, branch_id: car.branch_id, notes: car.notes || "", is_konsinyasi: car.is_konsinyasi || false,
            consign_owner: car.consign_owner || "", consign_price: car.consign_price || "",
            asking_cash_price: car.asking_cash_price || "", asking_credit_price: car.asking_credit_price || "",
            packages: (car.credit_packages || []).map((p) => ({
              leasing_name: p.leasing_name, tdp: String(p.tdp || ""),
              installments: (p.installments || []).map((i) => ({ amount: String(i.amount), tenor: String(i.tenor) })),
            })),
          }
        : {
            merk: "", model: "", year: new Date().getFullYear(), plate: "", color: "", km: 0, branch_id: (user?.role === "admin_cabang" ? user.branch_id : branches[0]?.id) || "", notes: "", is_konsinyasi: false, consign_owner: "", consign_price: "",
            asking_cash_price: "", asking_credit_price: "", packages: [],
          });
    }
  }, [open, car, branches, user]);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addPackage = () => setForm((f) => ({ ...f, packages: [...f.packages, { leasing_name: "", tdp: "", installments: [{ amount: "", tenor: "" }] }] }));
  const removePkg = (pi) => setForm((f) => ({ ...f, packages: f.packages.filter((_, i) => i !== pi) }));
  const setPkg = (pi, k, v) => setForm((f) => ({ ...f, packages: f.packages.map((p, i) => (i === pi ? { ...p, [k]: v } : p)) }));
  const addIns = (pi) => setForm((f) => ({ ...f, packages: f.packages.map((p, i) => (i === pi ? { ...p, installments: [...p.installments, { amount: "", tenor: "" }] } : p)) }));
  const removeIns = (pi, ii) => setForm((f) => ({ ...f, packages: f.packages.map((p, i) => (i === pi ? { ...p, installments: p.installments.filter((_, j) => j !== ii) } : p)) }));
  const setIns = (pi, ii, k, v) => setForm((f) => ({ ...f, packages: f.packages.map((p, i) => (i === pi ? { ...p, installments: p.installments.map((ins, j) => (j === ii ? { ...ins, [k]: v } : ins)) } : p)) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form, year: parseInt(form.year) || 0, km: parseInt(form.km) || 0,
        asking_cash_price: parseFloat(form.asking_cash_price) || 0,
        consign_price: form.is_konsinyasi ? parseFloat(form.consign_price) || 0 : 0,
        consign_owner: form.is_konsinyasi ? form.consign_owner : "",
        asking_credit_price: parseFloat(form.asking_credit_price) || 0,
        credit_packages: (form.packages || []).filter((p) => p.leasing_name).map((p) => ({
          leasing_name: p.leasing_name, tdp: parseFloat(p.tdp) || 0,
          installments: (p.installments || []).filter((i) => i.amount && i.tenor).map((i) => ({ amount: parseFloat(i.amount), tenor: parseInt(i.tenor) })),
        })),
      };
      delete payload.packages;
      let carId = car?.id;
      if (car) {
        await api.put(`/cars/${car.id}`, payload);
      } else {
        const r = await api.post("/cars", payload);
        carId = r.data.id;
      }
      for (const f of photo) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/cars/${carId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(car ? "Data mobil diperbarui" : "Mobil berhasil ditambahkan");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">{car ? "Edit Mobil" : "Tambah Mobil"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="car-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Merk</Label>
              <Input data-testid="car-merk-input" required value={form.merk} onChange={(e) => set("merk", e.target.value)} placeholder="Toyota" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Model</Label>
              <Input data-testid="car-model-input" required value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="Avanza G" className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Tahun</Label>
              <Input data-testid="car-year-input" type="number" required value={form.year} onChange={(e) => set("year", e.target.value)} className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Warna</Label>
              <Input data-testid="car-color-input" value={form.color} onChange={(e) => set("color", e.target.value)} placeholder="Hitam" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>KM</Label>
              <Input data-testid="car-km-input" type="number" min="0" value={form.km} onChange={(e) => set("km", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Plat Nomor</Label>
              <Input data-testid="car-plate-input" value={form.plate} onChange={(e) => set("plate", e.target.value)} placeholder="B 1234 ABC" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Cabang</Label>
              <Select value={form.branch_id} onValueChange={(v) => set("branch_id", v)}>
                <SelectTrigger data-testid="car-branch-select" className={inputCls}><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                  {visibleBranches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              data-testid="car-konsinyasi-checkbox"
              checked={form.is_konsinyasi}
              onCheckedChange={(v) => setForm((f) => ({ ...f, is_konsinyasi: !!v }))}
            />
            <Label className="text-sm text-zinc-300 cursor-pointer">Stok Konsinyasi (titipan rekan — tanpa modal pembelian)</Label>
          </div>
          {form.is_konsinyasi && (
            <div className="grid grid-cols-2 gap-3" data-testid="car-konsinyasi-fields">
              <div className="space-y-2">
                <Label className={labelCls}>Nama Pemilik</Label>
                <Input data-testid="car-consign-owner-input" value={form.consign_owner} onChange={(e) => set("consign_owner", e.target.value)} placeholder="Pak Budi" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Harga Titip ke Pemilik (Rp)</Label>
                <Input data-testid="car-consign-price-input" type="number" min="0" value={form.consign_price} onChange={(e) => set("consign_price", e.target.value)} placeholder="150000000" className={inputCls} />
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label className={labelCls}>Foto Mobil (bisa lebih dari satu)</Label>
            <Input
              data-testid="car-photo-input"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setPhoto(Array.from(e.target.files || []))}
              className="bg-[#0F0F12] border-[#1A1A22] file:bg-[#121218] file:text-zinc-200 file:border-0 file:rounded-sm file:px-3 file:py-1 file:mr-3 text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Catatan</Label>
            <Input data-testid="car-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Kondisi, kelengkapan dokumen, dll" className={inputCls} />
          </div>
          <div className="pt-3 border-t border-[#1A1A22] space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-blue-500">Harga Jual & Paket Kredit (Pricelist)</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className={labelCls}>Harga Cash (Rp)</Label>
                <Input data-testid="car-cash-price-input" type="number" min="0" value={form.asking_cash_price} onChange={(e) => set("asking_cash_price", e.target.value)} placeholder="200000000" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Harga Kredit (Rp)</Label>
                <Input data-testid="car-credit-price-input" type="number" min="0" value={form.asking_credit_price} onChange={(e) => set("asking_credit_price", e.target.value)} placeholder="185000000" className={inputCls} />
              </div>
            </div>
            {form.packages.map((p, pi) => (
              <div key={pi} className="p-3 border border-[#1A1A22] rounded-md space-y-2" data-testid={`pkg-${pi}`}>
                <div className="flex gap-2">
                  <Input data-testid={`pkg-leasing-${pi}`} value={p.leasing_name} onChange={(e) => setPkg(pi, "leasing_name", e.target.value)} placeholder="Nama leasing (MUF, BCAF, dll)" className={inputCls} />
                  <button type="button" onClick={() => removePkg(pi)} className="text-zinc-600 hover:text-rose-500 px-2 transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="space-y-1">
                  <Label className={labelCls}>TDP (Rp)</Label>
                  <Input data-testid={`pkg-tdp-${pi}`} type="number" min="0" value={p.tdp} onChange={(e) => setPkg(pi, "tdp", e.target.value)} placeholder="9000000" className={inputCls} />
                </div>
                {p.installments.map((ins, ii) => (
                  <div key={ii} className="flex gap-2 items-center">
                    <Input data-testid={`pkg-ins-amount-${pi}-${ii}`} type="number" min="0" value={ins.amount} onChange={(e) => setIns(pi, ii, "amount", e.target.value)} placeholder="Angsuran" className={inputCls} />
                    <span className="text-zinc-500 text-sm">×</span>
                    <Input data-testid={`pkg-ins-tenor-${pi}-${ii}`} type="number" min="1" value={ins.tenor} onChange={(e) => setIns(pi, ii, "tenor", e.target.value)} placeholder="Bln" className={`${inputCls} w-20`} />
                    <button type="button" onClick={() => removeIns(pi, ii)} className="text-zinc-600 hover:text-rose-500 transition-colors duration-200"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <button type="button" data-testid={`pkg-add-ins-${pi}`} onClick={() => addIns(pi)} className="text-xs text-blue-500 hover:text-blue-400 transition-colors duration-200">+ Angsuran</button>
              </div>
            ))}
            <button type="button" data-testid="add-package-button" onClick={addPackage} className="text-sm text-blue-500 hover:text-blue-400 font-medium transition-colors duration-200">+ Paket Leasing</button>
          </div>
          <Button data-testid="car-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : car ? "Simpan Perubahan" : "Tambah Mobil"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Cars() {
  const { user } = useAuth();
  const [cars, setCars] = useState(null);
  const [branches, setBranches] = useState([]);
  const [status, setStatus] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const reqSeq = useRef(0);

  const load = () => {
    const seq = ++reqSeq.current;
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (branchFilter !== "all") params.set("branch_id", branchFilter);
    if (q.trim()) params.set("search", q.trim());
    api.get(`/cars?${params}`)
      .then((r) => { if (seq === reqSeq.current) setCars(r.data); })
      .catch((e) => { if (seq === reqSeq.current) toast.error(apiError(e)); })
      .finally(() => { if (seq === reqSeq.current) setSearching(false); });
  };

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)); }, []);
  useEffect(() => {
    setSearching(true);
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [status, branchFilter, q]);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "-";

  return (
    <div data-testid="cars-page">
      <PageHeader title="Stok Mobil" subtitle="Kelola inventaris mobil bekas Anda">
        {can(user, "kelola_mobil") && (
          <Button data-testid="add-car-button" onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold rounded-md">
            <Plus className="w-4 h-4 mr-1" /> Tambah Mobil
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-56 max-w-sm">
          {searching ? <Loader2 data-testid="car-search-loading" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 animate-spin" /> : <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />}
          <Input
            data-testid="car-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari merk, model, plat..."
            className={`${inputCls} pl-9`}
          />
        </div>
        <Select value={branchFilter} onValueChange={setBranchFilter}>
          <SelectTrigger data-testid="car-branch-filter" className="w-48 h-10 bg-[#0F0F12] border-[#1A1A22]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
            <SelectItem value="all">Semua Lokasi</SelectItem>
            {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="car-status-filter" className="w-48 h-10 bg-[#0F0F12] border-[#1A1A22]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
            <SelectItem value="all">Semua Status</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!cars ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => <div key={i} className="h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />)}
        </div>
      ) : cars.length === 0 ? (
        <EmptyState icon={Car} title="Belum ada mobil" desc="Tambahkan mobil pertama Anda untuk mulai mencatat transaksi" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {cars.map((c, i) => (
            <Link
              key={c.id}
              to={`/mobil/${c.id}`}
              data-testid={`car-card-${c.id}`}
              className="fade-up group bg-[#0A0A0C] border border-[#1A1A22] rounded-md overflow-hidden hover:-translate-y-[2px] hover:border-[#26262E] transition-all duration-200"
              style={{ animationDelay: `${i * 50}ms`, transitionProperty: "transform, border-color" }}
            >
              <div className="aspect-video bg-[#121218] relative overflow-hidden">
                {c.photo_path ? (
                  <img src={fileUrl(c.photo_path, 600)} alt={`${c.merk} ${c.model}`} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Car className="w-10 h-10 text-zinc-700" />
                  </div>
                )}
                <div className="absolute top-2 left-2 flex gap-1.5"><StatusBadge status={c.status} />{c.is_konsinyasi && <span data-testid={`konsinyasi-badge-${c.id}`} className="px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-500 border border-emerald-500/25">Konsinyasi</span>}</div>
              </div>
              <div className="p-4">
                <p className="font-heading font-bold text-zinc-50 truncate">{carLabel(c)}</p>
                {c.asking_cash_price ? <p className="text-sm font-mono tabular-nums text-blue-500 mt-1">Cash {fmtIDR(c.asking_cash_price)}</p> : null}
                <p className="text-xs text-zinc-500 mt-1">{c.year} · {c.color || "-"} · {new Intl.NumberFormat("id-ID").format(c.km || 0)} km</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-zinc-400">{c.plate || "Tanpa plat"}</span>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500">{branchName(c.branch_id)}</span>
                </div>
                {c.estimasi_biaya ? (
                  <p data-testid={`car-estimasi-${c.id}`} className="text-xs text-zinc-400 mt-2 pt-2 border-t border-[#1A1A22] flex justify-between">
                    Estimasi Biaya <span className="font-mono tabular-nums text-amber-500">{fmtIDR(c.estimasi_biaya)}</span>
                  </p>
                ) : null}
                {c.modal != null && can(user, "lihat_hpp") && (
                  <p data-testid={`car-modal-${c.id}`} className="text-xs text-zinc-400 mt-2 pt-2 border-t border-[#1A1A22] flex justify-between">
                    Modal <span className="font-mono tabular-nums text-blue-500">{fmtIDR(c.modal)}</span>
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      <CarFormDialog open={dialogOpen} onOpenChange={setDialogOpen} branches={branches} car={null} onSaved={load} />
    </div>
  );
}

export { CarFormDialog };
